/**
 * Defines what's available in the code sandbox environment.
 * This is shared between Monaco editor (for IntelliSense) and the AI assistant.
 */
import { MAX_CODE_SIZE_BYTES } from './postcall-workflow-activities/action-types/code-code-1';
/**
 * TypeScript type definitions for Monaco editor.
 * These tell the editor what globals and types are available.
 */
export const CODE_SANDBOX_TYPE_DEFINITIONS = `
// ============================================
// CODE SANDBOX ENVIRONMENT
// ============================================
// This code runs in an isolated V8 sandbox with ES2020 support.
// NO Node.js APIs, NO DOM APIs, NO external libraries.
// Only pure JavaScript/TypeScript is available.

// The input object is typed based on your Input Schema configuration
declare const input: Input;

// ============================================
// AVAILABLE GLOBALS
// ============================================

// JSON is available
declare const JSON: {
  parse(text: string): any;
  stringify(value: any, replacer?: any, space?: number): string;
};

// Math is available
declare const Math: {
  abs(x: number): number;
  ceil(x: number): number;
  floor(x: number): number;
  round(x: number): number;
  max(...values: number[]): number;
  min(...values: number[]): number;
  pow(x: number, y: number): number;
  sqrt(x: number): number;
  random(): number;
  PI: number;
  E: number;
};

// Console is NOT available (no logging)
// setTimeout/setInterval are NOT available
// fetch/XMLHttpRequest are NOT available
// require/import are NOT available
// process/Buffer are NOT available

// ============================================
// DATE IS AVAILABLE
// ============================================
declare class Date {
  constructor();
  constructor(value: number | string);
  constructor(year: number, month: number, date?: number, hours?: number, minutes?: number, seconds?: number, ms?: number);

  getTime(): number;
  getFullYear(): number;
  getMonth(): number;
  getDate(): number;
  getDay(): number;
  getHours(): number;
  getMinutes(): number;
  getSeconds(): number;
  getMilliseconds(): number;

  getUTCFullYear(): number;
  getUTCMonth(): number;
  getUTCDate(): number;
  getUTCDay(): number;
  getUTCHours(): number;
  getUTCMinutes(): number;
  getUTCSeconds(): number;
  getUTCMilliseconds(): number;

  setTime(time: number): number;
  setFullYear(year: number, month?: number, date?: number): number;
  setMonth(month: number, date?: number): number;
  setDate(date: number): number;
  setHours(hours: number, min?: number, sec?: number, ms?: number): number;
  setMinutes(min: number, sec?: number, ms?: number): number;
  setSeconds(sec: number, ms?: number): number;
  setMilliseconds(ms: number): number;

  toISOString(): string;
  toJSON(): string;
  toDateString(): string;
  toTimeString(): string;
  toString(): string;
  valueOf(): number;

  static now(): number;
  static parse(s: string): number;
  static UTC(year: number, month: number, date?: number, hours?: number, minutes?: number, seconds?: number, ms?: number): number;
}

// ============================================
// AVAILABLE STRING METHODS
// ============================================
interface String {
  length: number;
  charAt(pos: number): string;
  charCodeAt(index: number): number;
  concat(...strings: string[]): string;
  includes(searchString: string, position?: number): boolean;
  endsWith(searchString: string, endPosition?: number): boolean;
  indexOf(searchString: string, position?: number): number;
  lastIndexOf(searchString: string, position?: number): number;
  match(regexp: RegExp): RegExpMatchArray | null;
  replace(searchValue: string | RegExp, replaceValue: string): string;
  replaceAll(searchValue: string | RegExp, replaceValue: string): string;
  search(regexp: RegExp): number;
  slice(start?: number, end?: number): string;
  split(separator: string | RegExp, limit?: number): string[];
  startsWith(searchString: string, position?: number): boolean;
  substring(start: number, end?: number): string;
  toLowerCase(): string;
  toUpperCase(): string;
  trim(): string;
  trimStart(): string;
  trimEnd(): string;
  padStart(maxLength: number, fillString?: string): string;
  padEnd(maxLength: number, fillString?: string): string;
  repeat(count: number): string;
}

// ============================================
// AVAILABLE ARRAY METHODS
// ============================================
interface Array<T> {
  length: number;
  push(...items: T[]): number;
  pop(): T | undefined;
  shift(): T | undefined;
  unshift(...items: T[]): number;
  concat(...items: (T | T[])[]): T[];
  join(separator?: string): string;
  slice(start?: number, end?: number): T[];
  splice(start: number, deleteCount?: number, ...items: T[]): T[];
  indexOf(searchElement: T, fromIndex?: number): number;
  lastIndexOf(searchElement: T, fromIndex?: number): number;
  includes(searchElement: T, fromIndex?: number): boolean;
  find(predicate: (value: T, index: number, obj: T[]) => boolean): T | undefined;
  findIndex(predicate: (value: T, index: number, obj: T[]) => boolean): number;
  filter(predicate: (value: T, index: number, array: T[]) => boolean): T[];
  map<U>(callbackfn: (value: T, index: number, array: T[]) => U): U[];
  reduce<U>(callbackfn: (previousValue: U, currentValue: T, currentIndex: number, array: T[]) => U, initialValue: U): U;
  reduceRight<U>(callbackfn: (previousValue: U, currentValue: T, currentIndex: number, array: T[]) => U, initialValue: U): U;
  forEach(callbackfn: (value: T, index: number, array: T[]) => void): void;
  every(predicate: (value: T, index: number, array: T[]) => boolean): boolean;
  some(predicate: (value: T, index: number, array: T[]) => boolean): boolean;
  sort(compareFn?: (a: T, b: T) => number): T[];
  reverse(): T[];
  flat<D extends number = 1>(depth?: D): any[];
  flatMap<U>(callback: (value: T, index: number, array: T[]) => U | U[]): U[];
}

// ============================================
// AVAILABLE OBJECT METHODS
// ============================================
interface ObjectConstructor {
  keys(o: object): string[];
  values(o: object): any[];
  entries(o: object): [string, any][];
  assign<T, U>(target: T, source: U): T & U;
  fromEntries(entries: Iterable<readonly [PropertyKey, any]>): any;
}
declare const Object: ObjectConstructor;

// ============================================
// AVAILABLE NUMBER METHODS
// ============================================
interface Number {
  toFixed(fractionDigits?: number): string;
  toPrecision(precision?: number): string;
  toString(radix?: number): string;
}

interface NumberConstructor {
  isNaN(number: number): boolean;
  isFinite(number: number): boolean;
  isInteger(number: number): boolean;
  parseInt(string: string, radix?: number): number;
  parseFloat(string: string): number;
}
declare const Number: NumberConstructor;

// ============================================
// REGEX SUPPORT
// ============================================
interface RegExp {
  test(string: string): boolean;
  exec(string: string): RegExpExecArray | null;
}

// ============================================
// ERROR HANDLING
// ============================================
declare class Error {
  constructor(message?: string);
  message: string;
  name: string;
}
`;
/**
 * Human-readable description of the sandbox environment.
 * Used in the AI assistant's system prompt.
 */
export const CODE_SANDBOX_DESCRIPTION = `
## Code Sandbox Environment

The code runs in an **isolated V8 sandbox** with the following characteristics:

### Available Features (ES2020):
- **Variables**: \`const\`, \`let\`, \`var\`
- **Functions**: Regular functions, arrow functions, async/await (but no external async operations)
- **Control flow**: \`if/else\`, \`switch\`, \`for\`, \`while\`, \`do-while\`, \`for...of\`, \`for...in\`
- **Operators**: All standard operators including \`??\`, \`?.\`, spread (\`...\`)
- **Destructuring**: Object and array destructuring
- **Template literals**: Backtick strings with \${} interpolation
- **Classes**: ES6 class syntax
- **Modules**: NOT available (no import/export)

### Available Global Objects:
- **JSON**: \`JSON.parse()\`, \`JSON.stringify()\`
- **Math**: \`Math.abs()\`, \`Math.floor()\`, \`Math.ceil()\`, \`Math.round()\`, \`Math.max()\`, \`Math.min()\`, \`Math.pow()\`, \`Math.sqrt()\`, \`Math.random()\`, \`Math.PI\`
- **Date**: \`new Date()\`, \`Date.now()\`, \`Date.parse()\`, and all standard Date methods (\`getFullYear()\`, \`getMonth()\`, \`getDate()\`, \`getTime()\`, \`toISOString()\`, etc.)
- **Object**: \`Object.keys()\`, \`Object.values()\`, \`Object.entries()\`, \`Object.assign()\`, \`Object.fromEntries()\`
- **Number**: \`Number.isNaN()\`, \`Number.isFinite()\`, \`Number.isInteger()\`, \`Number.parseInt()\`, \`Number.parseFloat()\`
- **Array methods**: \`map\`, \`filter\`, \`reduce\`, \`find\`, \`findIndex\`, \`forEach\`, \`some\`, \`every\`, \`includes\`, \`flat\`, \`flatMap\`, \`sort\`, \`reverse\`, etc.
- **String methods**: \`split\`, \`join\`, \`slice\`, \`substring\`, \`replace\`, \`replaceAll\`, \`trim\`, \`toLowerCase\`, \`toUpperCase\`, \`includes\`, \`startsWith\`, \`endsWith\`, \`padStart\`, \`padEnd\`, etc.
- **RegExp**: Regular expressions with \`test()\` and \`exec()\`
- **Error**: Can throw and catch errors

### NOT Available (Security Restrictions):
- ❌ **console** - No logging
- ❌ **setTimeout/setInterval** - No timers
- ❌ **fetch/XMLHttpRequest** - No network requests
- ❌ **require/import** - No external modules
- ❌ **process/Buffer** - No Node.js APIs
- ❌ **document/window** - No DOM APIs
- ❌ **fs/path** - No filesystem access

### Execution Limits:
- **Timeout**: 5 seconds maximum execution time
- **Memory**: 128MB maximum memory usage
- **Input size**: 1MB maximum
- **Output size**: 1MB maximum
- **Code size**: ${MAX_CODE_SIZE_BYTES / 1024}KB maximum

### Required Function Signature:
The code MUST define a \`run\` function that takes the input object and returns a result:

\`\`\`typescript
function run(input: Input) {
  // Your code here
  // Access input fields via input.fieldName

  return {
    // Your output object
  };
}
\`\`\`
`;
//# sourceMappingURL=code-sandbox-environment.js.map